import React from 'react';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { DfcSurface } from '../../../../../Utils/dfcSurface';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import useDfcThemeSwitch from '../../hooks/useDfcThemeSwitch';
import { Glyph, Orb, Star, SwitchPill, Thumb } from './themeSwitchStyle';

interface ThemeSwitchProps {
  surface: DfcSurface;
  /** "pill" — the day/night track on desktop; "orb" — the compact round control on mobile. */
  variant: 'pill' | 'orb';
}

/**
 * The village header's light / dark switch. Day: a sun on a pale track. Night: the thumb rolls
 * across, turns green and becomes a crescent while a few stars come out. On mobile the same
 * sun-to-moon turn happens inside a single round control the size of the header's other
 * buttons. Either way it is one `role="switch"` with a 44px hit area.
 */
const ThemeSwitch: React.FC<ThemeSwitchProps> = ({ surface, variant }) => {
  const { isDark, toggle } = useDfcThemeSwitch();
  const copy = useFitnessVillageCopy().header;

  const glyphs = (
    <>
      <Glyph aria-hidden visible={!isDark}>
        <LightModeRoundedIcon />
      </Glyph>
      <Glyph aria-hidden visible={isDark}>
        <DarkModeRoundedIcon />
      </Glyph>
    </>
  );

  const shared = {
    role: 'switch',
    'aria-checked': isDark,
    'aria-label': copy.darkMode,
    onClick: toggle,
    'data-testid': 'theme-switch',
  };

  if (variant === 'orb') {
    return (
      <Orb surface={surface} dark={isDark} {...shared}>
        {glyphs}
        <Star aria-hidden dark={isDark} at="orb" />
      </Orb>
    );
  }

  return (
    <SwitchPill surface={surface} dark={isDark} {...shared}>
      <Star aria-hidden dark={isDark} at="a" />
      <Star aria-hidden dark={isDark} at="b" />
      <Star aria-hidden dark={isDark} at="c" />
      <Thumb surface={surface} dark={isDark}>
        {glyphs}
      </Thumb>
    </SwitchPill>
  );
};

export default ThemeSwitch;
